import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    SimpleChange,
    ViewChild,
    ViewEncapsulation,
    Inject
} from '@angular/core';
import { FullCalendarComponent as FCComponent, CalendarOptions } from '@fullcalendar/angular';

import { IAvailabilityWindowSchema, IStartEndTimeWindowSchema, IWorkDaysSchema, WeekDay } from 'common-lib';

// TODO-COMMON
import { TimeRangeService } from '../../../services/time-range/time-range.service';
import { UtilsService } from 'common-lib/services';

import moment_ from 'moment-timezone';
const moment = moment_;
import * as _ from 'lodash';
interface IFullCalendarEvent {
    id: string;
    start: Date;
    display: string;
    title?: string;
    allDay?: boolean;
    backgroundColor?: string;
    classNames?: string[];
}

interface ITimeInterval {
    value: string;
    name: string;
}

type CalendarView = 'listWeek' | 'dayGridWeek';

@Component({
    selector: 'wizard-availability-calendar',
    templateUrl: './availability-calendar.component.html',
    styleUrls: ['./availability-calendar.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AvailabilityCalendarComponent implements OnInit {
    @Input() availabilityWindows: IAvailabilityWindowSchema;
    @Input() meetingLength = 60;
    @Input() isBasicUser = false;
    @Input() isReadOnly = false;
    @Input() timezone = moment.tz.guess();
    @Output() onSave = new EventEmitter();
    @ViewChild('calendar', { static: true }) calendarComponent: FCComponent;
    @ViewChild('calendarContainer', { static: true }) containerComponent: ElementRef;
    private intervalLength = 15;
    private minIntervalBetweenEvents = 30;
    private untouchedAvailabilityWindow?: IAvailabilityWindowSchema;
    private currentCalendarView: CalendarView = 'dayGridWeek';
    private calendarViewBreakpoint = 750;
    private resizeObserver: ResizeObserver;
    public timeIntervals: ITimeInterval[];
    public showModal = false;
    public canAddMoreTimeRanges = true;
    public isSelectedDateAvailable = true;
    public selectedWeekday: WeekDay = 'monday';
    public calendarOptions?: CalendarOptions;
    /** used for Meeting Types view */
    public isCalendarReady = false;

    constructor(private timeRangeService: TimeRangeService, @Inject(UtilsService) private utilsService: UtilsService) {}

    ngOnInit() {
        this.timeIntervals = this.utilsService.createSemanticTimeIntervals(this.intervalLength);
        this.initCalendar();
        this.initResizeObserver();
        this.populateCalendarWithEvents(this.availabilityWindows);
    }

    ngAfterViewInit() {
        let calendarApi = this.calendarComponent.getApi();

        setTimeout(() => {
            calendarApi.updateSize();
            this.isCalendarReady = true;
        }, 0);
    }

    /** changes is called when new windows are passed down e.g. changing meeting type in Wizard scheduler */
    ngOnChanges(changes: { availabilityWindows: SimpleChange }) {
        if (changes.availabilityWindows) {
            this.populateCalendarWithEvents(this.availabilityWindows);
        }
    }

    ngOnDestroy() {
        this.resizeObserver.unobserve(this.containerComponent.nativeElement);
    }

    private initCalendar() {
        this.calendarOptions = {
            initialView: 'dayGridWeek',
            headerToolbar: false,
            dayHeaderFormat: {
                weekday: 'short'
            },
            contentHeight: 'auto',
            eventTextColor: '#000',
            nowIndicator: false,
            dateClick: (_value) => {
                this.isReadOnly ? false : this.handleShowModal(_value.date);
            },
            eventClick: (eventInfo) => {
                this.isReadOnly ? false : this.handleShowModal(eventInfo.event.start);
            },
            displayEventTime: false
        };
    }

    private initResizeObserver() {
        this.resizeObserver = new ResizeObserver((entries) => {
            const width = entries[0].contentRect.width;
            if (width < this.calendarViewBreakpoint && this.currentCalendarView === 'dayGridWeek') {
                this.changeCalendarView('listWeek');
            } else if (width >= this.calendarViewBreakpoint && this.currentCalendarView === 'listWeek') {
                this.changeCalendarView('dayGridWeek');
            }
        });

        this.resizeObserver.observe(this.containerComponent.nativeElement);
    }

    private changeCalendarView(view: CalendarView) {
        this.currentCalendarView = view;
        let calendarApi = this.calendarComponent.getApi();
        calendarApi.changeView(view);
        this.populateCalendarWithEvents(this.availabilityWindows);
        if (view === 'listWeek') {
            this.calendarOptions = { ...this.calendarOptions, listDaySideFormat: false };
        }
    }

    private populateCalendarWithEvents(availabilityWindows: IAvailabilityWindowSchema) {
        const unavailableDays = this.getUnavailableDays(availabilityWindows);
        const unavailableDayEvents = this.createDisabledBackgroundEvents(unavailableDays, this.currentCalendarView);
        const calendarEvents = this.transformEventsIn(availabilityWindows, unavailableDays);

        this.calendarOptions = {
            ...this.calendarOptions,
            events: [...unavailableDayEvents, ...calendarEvents]
        };
    }

    private transformEventsIn(data: IAvailabilityWindowSchema, daysToHide: WeekDay[]) {
        let allEvents: IFullCalendarEvent[] = [];
        for (const [key, value] of Object.entries(data)) {
            if (value[0] && value[0].start) {
                const weekday = key as WeekDay;
                const hideEvent = daysToHide.includes(weekday);
                const eventsPerDay = this.createEvents(weekday, value, hideEvent);
                allEvents = allEvents.concat(eventsPerDay);
            }
        }

        return allEvents;
    }

    private createWorkDays(availabilityWindows: IAvailabilityWindowSchema) {
        const weekDays = Object.keys(availabilityWindows);

        const availableDays = weekDays.reduce((daysObj, currentDay) => {
            if (availabilityWindows[currentDay].length) {
                daysObj[currentDay] = true;
            } else {
                daysObj[currentDay] = false;
            }
            return daysObj;
        }, {} as IWorkDaysSchema);

        return availableDays;
    }

    /** @params startTime must be in format H:mm */
    private createStartDateFromWeekdayAndTime(weekday: WeekDay, startTime: string) {
        const splitTime = /^(.+):(.+)/g.exec(startTime);
        const hours = splitTime[1];
        const minutes = splitTime[2];

        return moment(weekday, 'dddd').hours(parseInt(hours)).minute(parseInt(minutes)).toDate();
    }

    /** Transform array of IStartEndTimeWindowSchema to FullCalendar-compatible data model  */
    private createEvents(weekday: WeekDay, value: IStartEndTimeWindowSchema[], hideEvent: boolean) {
        return value.map((time) => {
            const start = this.createStartDateFromWeekdayAndTime(weekday, time.start);

            /** convert to h:mm for display purposes only */
            const startTimeForDisplay = moment(time.start, 'HH:mm').format('h:mma');
            const endTimeForDisplay = moment(time.end, 'HH:mm').format('h:mma');

            return {
                id: `${weekday}-${time.start}`,
                start,
                title: `${startTimeForDisplay} - ${endTimeForDisplay}`,
                display: hideEvent ? 'none' : 'list-item'
            };
        });
    }

    private getUnavailableDays(availabilityWindows) {
        const workDays = this.createWorkDays(availabilityWindows);
        return _.filter(
            _.map(workDays, (isAvailable: boolean, day: WeekDay) => {
                return isAvailable ? undefined : day;
            }),
            _.identity
        );
    }

    private createDisabledBackgroundEvents(unavailableDays: WeekDay[], currentCalendarView: CalendarView) {
        return unavailableDays.map((unavailableDay) => {
            const weekdayToMoment = moment(unavailableDay, 'dddd');
            return {
                id: weekdayToMoment.format(),
                start: weekdayToMoment.toDate(),
                allDay: true,
                display: currentCalendarView === 'dayGridWeek' ? 'background' : 'block',
                title: 'Not available',
                backgroundColor: '#ededed',
                classNames: ['unavailable-event']
            };
        });
    }

    /** Create a copy of the original availabilityWindow and workDays. Only save them when user hits Save */
    private handleShowModal(selectedDate: Date) {
        const momentDate = moment(selectedDate);
        this.selectedWeekday = momentDate.format('dddd').toLowerCase() as WeekDay;

        const numberOfTimeRanges = this.availabilityWindows[this.selectedWeekday].length;
        const lastEndHour = this.availabilityWindows[this.selectedWeekday][numberOfTimeRanges - 1]?.end;
        this.canAddMoreTimeRanges = lastEndHour
            ? this.checkHasTimeForMoreRanges(moment(lastEndHour, 'HH:mm'), this.meetingLength, this.selectedWeekday)
            : true;

        this.isSelectedDateAvailable = !!this.availabilityWindows[this.selectedWeekday].length;
        this.untouchedAvailabilityWindow = _.cloneDeep(this.availabilityWindows);
        this.showModal = true;
    }

    private restoreAvailabilityWindows() {
        if (this.untouchedAvailabilityWindow) {
            this.availabilityWindows = this.untouchedAvailabilityWindow;
        }
        this.untouchedAvailabilityWindow = null;
    }

    public handleCancelChanges() {
        this.showModal = false;
        this.restoreAvailabilityWindows();
    }

    private checkHasTimeForMoreRanges(endHour: moment.Moment, meetingLength: number, weekday: WeekDay) {
        if (this.availabilityWindows[weekday].length >= 5) return false;

        const nextStartHour = endHour.add(meetingLength + this.minIntervalBetweenEvents, 'minutes');
        const endOfDay = moment(endHour).endOf('day');
        return !nextStartHour.isSameOrAfter(endOfDay);
    }

    public handleAddTimeRange(selectedWeekday: WeekDay) {
        const timeRanges = this.availabilityWindows[selectedWeekday];
        let nextStartHour = moment('09:00', 'HH:mm');
        let nextEndHour = moment('17:00', 'HH:mm');

        if (timeRanges.length) {
            const lastEndHour = timeRanges[timeRanges.length - 1].end;

            nextStartHour = moment(lastEndHour, 'HH:mm').add(this.minIntervalBetweenEvents, 'minutes');
            nextEndHour = moment(nextStartHour).add(this.meetingLength, 'minutes');
        }

        this.availabilityWindows[selectedWeekday].push({
            start: nextStartHour.format('HH:mm'),
            end: nextEndHour.format('HH:mm')
        });

        this.canAddMoreTimeRanges = this.checkHasTimeForMoreRanges(nextEndHour, this.meetingLength, selectedWeekday);
    }

    public handleUpdateTimeRange(value: { index: number; weekday: WeekDay; start?: string; end?: string }) {
        const availabilityWindows = this.availabilityWindows;

        if (value.start) {
            availabilityWindows[value.weekday][value.index].start = value.start;
        } else if (value.end) {
            availabilityWindows[value.weekday][value.index].end = value.end;
            const endHour = moment(value.end, 'HH:mm');
            this.canAddMoreTimeRanges = this.checkHasTimeForMoreRanges(endHour, this.meetingLength, value.weekday);
        }

        this.timeRangeService.updateTimeRanges(availabilityWindows, value.weekday);
    }

    /** @params event.start is HH:mm */
    public handleRemoveTimeRange(value: { weekday: WeekDay; start: string; end: string; timeRangeIndex: number }) {
        const timeRanges = this.availabilityWindows[value.weekday];
        _.pullAt(timeRanges, value.timeRangeIndex);

        const lastEndHour = moment(timeRanges[timeRanges.length - 1].end, 'HH:mm');
        this.canAddMoreTimeRanges = this.checkHasTimeForMoreRanges(lastEndHour, this.meetingLength, value.weekday);
    }

    public handleUpdateAvailableDay(isAvailable: boolean, weekday: WeekDay) {
        this.isSelectedDateAvailable = isAvailable;
        if (!isAvailable) {
            this.availabilityWindows[weekday] = [];
        } else if (!this.availabilityWindows[weekday].length) {
            this.handleAddTimeRange(weekday);
        }
    }

    public handleSave() {
        this.populateCalendarWithEvents(this.availabilityWindows);

        this.onSave.emit({
            availabilityWindows: this.availabilityWindows
        });

        this.showModal = false;
    }
}
